import { takeLatest, put, call, select } from 'redux-saga/effects';
import { checkToken } from '../auth/sagas';
import { Urls } from '../../config/urls';
import { deleteTask, getCSVTask, getTask } from '../../services';
import {
  ClientsActionType,
  deleteClientError,
  deleteClientSuccess,
  exportClientsError,
  exportClientsSuccess,
  getClients,
  getClientsError,
  getClientsSuccess,
  getClientsSummary,
  getClientsSummarySuccess,
} from './actions';
import { PanelStore } from '../interfaces';
import { buildClientsFilterQuery } from '../../utils/build-clients-filter-query';
import { buildClientsCsv } from '../../utils/build-clients-csv';
import { hasClientSort, paginateClients, sortClients } from '../../utils/sort-clients';
import { Client } from './interfaces';
import { Filters } from '../interfaces';
import { computeClientsSummary, getClientsSummaryDateRange } from '../../utils/clients-summary';
import { Transaction } from '../transactions/interfaces';

function* fetchAllClients(filters?: Filters) {
  const state: PanelStore.RootState = yield select();
  const merchantId = state.dashboard.merchantMe?.id;
  const clients: Client[] = [];
  let page = 0;
  let totalPages = 1;
  let pageSize = 10;

  while (page < totalPages) {
    let params = `?page=${page}`;
    const filterParam = buildClientsFilterQuery(filters);
    params += filterParam ? `&${filterParam}` : '';

    const { response, error } = yield call(getTask, {
      url: Urls.api.allClients + params,
      token: state.auth.token,
    });

    if (!response?.data) {
      return { clients, error, pageSize };
    }

    if (page === 0) {
      pageSize = response.size ?? 10;
    }

    clients.push(...response.data);
    totalPages = response.totalPages ?? 1;
    page += 1;
  }

  return { clients, error: undefined, pageSize };
}

function* fetchCompletedTransactions(from: string, to: string, country?: string) {
  const state: PanelStore.RootState = yield select();
  const transactions: Transaction[] = [];
  let page = 0;
  let totalPages = 1;

  while (page < totalPages) {
    let params = `?page=${page}&status=COMPLETED&from=${from}&to=${to}`;
    if (country) {
      params += `&country=${encodeURIComponent(country)}`;
    }

    const { response, error } = yield call(getTask, {
      url: Urls.api.allTransactions + params,
      token: state.auth.token,
    });

    if (!response?.data) {
      return { transactions, error };
    }

    transactions.push(...response.data);
    totalPages = response.totalPages ?? 1;
    page += 1;
  }

  return { transactions, error: undefined };
}

function* getClientsSagaWorker(action: { page?: number; filters?: Filters; rowsPerPage?: number }) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();
  const pageSize = action.rowsPerPage ?? state.clients.allClients?.size ?? 10;

  let params = `?page=${action.page || 0}&page_size=${pageSize}`;
  const filterParam = buildClientsFilterQuery(action.filters);
  params += filterParam ? `&${filterParam}` : '';

  const { response, error } = yield call(getTask, {
    url: Urls.api.allClients + params,
    token: state.auth.token,
  });

  if (response) {
    yield put(getClientsSuccess(response));
    if (response.summary) {
      yield put(getClientsSummarySuccess(response.summary));
    }
  } else {
    yield put(getClientsError(error));
  }
}

function* exportClientsSagaWorker(action: { filters?: Record<string, string>; format?: string }) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const filterParam = buildClientsFilterQuery(action.filters);
  const params = filterParam ? `?${filterParam}` : '';

  const isExcel = action.format === 'excel';
  const url = isExcel ? Urls.api.exportClientsExcel : Urls.api.exportClientsCsv;

  const { response, error } = yield call(getCSVTask, {
    url: url + params,
    token: state.auth.token,
  });

  if (response?.text || response?.blob) {
    yield put(exportClientsSuccess({ ...response, format: action.format }));
    return;
  }

  if (!isExcel) {
    const { clients, error: fetchError } = yield* fetchAllClients(action.filters);

    if (clients.length > 0) {
      const sorted = sortClients(clients, action.filters);
      yield put(exportClientsSuccess({ text: buildClientsCsv(sorted), format: 'csv' }));
      return;
    }

    yield put(exportClientsError(error || fetchError));
    return;
  }

  yield put(exportClientsError(error));
}

function* getClientsSummarySagaWorker(action: { filters?: Filters }) {
  yield* checkToken();

  const summaryFilters: Filters = {
    ...(action.filters || {}),
    sort_by: '',
    sort_direction: '',
  };

  const { clients } = yield* fetchAllClients(summaryFilters);
  const { from, to } = getClientsSummaryDateRange();
  const { transactions } = yield* fetchCompletedTransactions(from, to, summaryFilters.country);
  yield put(getClientsSummarySuccess(computeClientsSummary(clients, transactions)));
}

function* deleteClientSagaWorker(action: {
  clientId: number;
  page?: number;
  filters?: Record<string, string>;
  rowsPerPage?: number;
}) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();
  const merchantId = state.dashboard.merchantMe?.id;

  const { response, error } = yield call(deleteTask, {
    url: Urls.api.deleteClient
      .replace(':id', `${merchantId || ''}`)
      .replace(':clientId', `${action.clientId}`),
    token: state.auth.token,
  });

  if (response && !error) {
    yield put(deleteClientSuccess(action.clientId));
    yield put(
      getClients(
        action.page ?? state.clients.allClients?.page ?? 0,
        action.filters,
        action.rowsPerPage ?? state.clients.allClients?.size
      )
    );
    yield put(getClientsSummary(action.filters));
  } else {
    yield put(deleteClientError(error));
  }
}

export function* getClientsSaga() {
  yield takeLatest(ClientsActionType.GET_CLIENTS, getClientsSagaWorker);
}

export function* exportClientsSaga() {
  yield takeLatest(ClientsActionType.EXPORT_CLIENTS, exportClientsSagaWorker);
}

export function* deleteClientSaga() {
  yield takeLatest(ClientsActionType.DELETE_CLIENT, deleteClientSagaWorker);
}

export function* getClientsSummarySaga() {
  yield takeLatest(ClientsActionType.GET_CLIENTS_SUMMARY, getClientsSummarySagaWorker);
}
